import {
  ActionIcon,
  Anchor,
  Box,
  Card,
  Group,
  Image,
  Menu,
  Stack,
  Text,
  rem
} from '@mantine/core'
import { IconDotsVertical, IconPencil, IconTrash } from '@tabler/icons-react'
import { MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import VpLogoSrc from '@/assets/vp-stripes.png'
import { LabelingTypeChip } from '@/components/annotations/LabelingTypes/LabelingTypeChip'
import { LabelGroup } from '@/components/labels/LabelGroup/LabelGroup'
import { AnimatedCard } from '@/components/ui-shared/AnimatedCard/AnimatedCard'
import { buildModelLink } from '@/router/paths'
import { ApplicationType } from '@/types/app'
import { MLModel } from '@/types/model'
import { ModelStatus } from '../ModelStatus/ModelStatus'
import { ModelTypeChip } from '../ModelTypeChip/ModelTypeChip'

type ModelCardProps = {
  model: MLModel
  applicationId: string
  applicationType: ApplicationType
  showActions?: boolean
  showEdit?: boolean
  showDelete?: boolean
  onRename: () => void
  onDelete: () => void
}

export const ModelCard = ({
  model,
  applicationId,
  applicationType,
  showActions,
  showEdit,
  showDelete,
  onRename,
  onDelete
}: ModelCardProps) => {
  const handleRename = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onRename()
  }

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onDelete()
  }

  return (
    <Anchor
      component={Link}
      to={buildModelLink(applicationId, model.id)}
      underline="never"
    >
      <AnimatedCard h={280} padding="sm">
        <Card.Section
          h={110}
          pos="relative"
          bg="gray.1"
          py="xs"
          pl="xs"
          pr="xl"
          withBorder
        >
          <LabelGroup
            labels={
              model.dataset_version?.labels ||
              model.base_model?.dataset_version?.labels ||
              []
            }
            maxLabelsShown={3}
          />

          <Box pos="absolute" top={8} right={8}>
            <Image
              src={model.root_model?.brand_logo || VpLogoSrc}
              h={24}
              w="auto"
            />
          </Box>
        </Card.Section>

        <Card.Section pos="relative" py="sm" inheritPadding>
          <Stack align="center" gap="sm">
            <Text ta="center" size="sm" fw="bold" pr="sm" lineClamp={2}>
              {model.name}
            </Text>

            {model.status && <ModelStatus status={model.status} />}

            {model.base_model && (
              <Text ta="center" size="xs" lineClamp={2}>
                <FormattedMessage
                  id="models.baseModelName"
                  values={{ name: model.base_model.name }}
                />
              </Text>
            )}

            <Group gap={4} justify="center">
              <LabelingTypeChip labelingType={model.labeling_type} />

              {applicationType === ApplicationType.MultiStage && (
                <ModelTypeChip modelType={model.model_type} />
              )}
            </Group>
          </Stack>

          {showActions && (
            <Menu position="bottom-start" width={200}>
              <Menu.Target>
                <ActionIcon
                  pos="absolute"
                  top={2}
                  right={2}
                  variant="subtle"
                  size="md"
                  radius="xl"
                  onClick={(event) => event.preventDefault()}
                >
                  <IconDotsVertical style={{ width: '80%', height: '80%' }} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {showEdit && (
                  <Menu.Item
                    leftSection={
                      <IconPencil style={{ width: rem(14), height: rem(14) }} />
                    }
                    onClick={handleRename}
                  >
                    <FormattedMessage id="rename" />
                  </Menu.Item>
                )}

                {showDelete && (
                  <Menu.Item
                    color="red"
                    leftSection={
                      <IconTrash style={{ width: rem(14), height: rem(14) }} />
                    }
                    onClick={handleDelete}
                  >
                    <FormattedMessage id="delete" />
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Card.Section>
      </AnimatedCard>
    </Anchor>
  )
}
